import { ColumnsType } from 'antd/es/table';
import { EmployeeData } from '../../../types';
import { useNavigate } from 'react-router-dom';
import { Paths } from '../../../routes';
import {
  annualReviewStatuses,
  convertNameToPath,
  getUserProfilesFromStorage,
  Statuses,
} from '../../../helper';
import { useAtom } from 'jotai';
import { userAtom } from '../../../store/atom';
import dayjs from 'dayjs';
import { formattedDateTime } from '../../../helper/functions';
import { useState, useEffect } from 'react';
import { getRandomColor } from '../../../components/create-edit-annual-review/helper';
import { getInitials } from '../../../components/create-edit-annual-review/helper';

export const Columns: ColumnsType = [
  {
    title: 'Employee Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a: EmployeeData, b: EmployeeData) => {
      return a.name?.trim().localeCompare(b.name?.trim());
    },
    render: (_, data) => <MenteeName data={data} />,
  },
  {
    title: 'Designation',
    dataIndex: 'designation',
    key: 'designation',
    sorter: (a: EmployeeData, b: EmployeeData) => {
      return a.designation?.trim().localeCompare(b.designation?.trim());
    },
  },
  {
    title: 'Tier',
    dataIndex: 'tier',
    key: 'tier',
    sorter: (a: EmployeeData, b: EmployeeData) =>
      Number(a.tier) - Number(b.tier),
    render: (tier) => <div className="text-center">{tier}</div>,
  },
  {
    title: 'Mentor Name',
    key: 'mentorEmail',
    render: (data: EmployeeData) => data.mentor?.name,
    sorter: (a: EmployeeData, b: EmployeeData) => {
      return a.mentor?.name?.trim().localeCompare(b.mentor?.name?.trim());
    },
  },
  {
    title: <div className="text-center">No. Of Objectives Set</div>,
    dataIndex: 'objectiveCount',
    key: 'objectiveCount',
    sorter: (a: EmployeeData, b: EmployeeData) =>
      Number(a.objectiveCount) - Number(b.objectiveCount),
    render: (objectiveCount) => (
      <div className="text-center">{objectiveCount}</div>
    ),
  },
  {
    title: 'Current Quarterly Review',
    key: 'cycleId',
    render: (data: EmployeeData) => <QuarterName data={data} />,
  },
  {
    title: 'Annual Reviews Involved In',
    key: 'annualData',
    render: (data: EmployeeData) => (
      <div className="flex justify-start items-center gap-1">
        {Number(data.tier) === 6 ? (
          <div className="text-center">N/A</div>
        ) : !data.annualData || data.annualData.length === 0 ? (
          <div className="text-center">-</div>
        ) : (
          data.annualData.map((item: any, idx: number) => (
            <AnnualCycle
              key={idx}
              data={data}
              year={item.year}
              status={item.status}
              annualCycleId={item.id}
            />
          ))
        )}
      </div>
    ),
  },
  {
    title: 'Last Accessed',
    dataIndex: 'lastAccessedDate',
    key: 'lastAccessedDate',
    sorter: (a: EmployeeData, b: EmployeeData) => {
      if (a.lastAccessedDate === null && b.lastAccessedDate === null) {
        return 0;
      }
      if (a.lastAccessedDate === null) {
        return 1; // Treat null as the largest value, so it appears last
      }
      if (b.lastAccessedDate === null) {
        return -1; // Treat null as the largest value, so it appears last
      }
      return dayjs(a.lastAccessedDate.trim()).isBefore(
        dayjs(b.lastAccessedDate.trim())
      )
        ? -1
        : 1;
    },
    render: (lastAccessedDate) =>
      lastAccessedDate ? (
        <div className="text-left ">{formattedDateTime(lastAccessedDate)}</div>
      ) : (
        <div className="text-center">-</div>
      ),
  },
];

export const MenteeName = ({
  data,
  quarterName,
  path,
  pathSate,
  state,
}: {
  data: EmployeeData;
  quarterName?: string;
  path?: string;
  pathSate?: any;
  state?: any;
}) => {
  const [userState, setUserState] = useAtom(userAtom);
  const navigate = useNavigate();
  const [url, setUrl] = useState<string>('');

  useEffect(() => {
    const fetchProfilePic = async () => {
      try {
        const res = await getUserProfilesFromStorage();
        const profile = res.data.find((u: any) => u.id === data.id);

        if (profile?.file instanceof Blob) {
          const objectUrl = URL.createObjectURL(profile.file);
          setUrl(objectUrl);
        }
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    fetchProfilePic();
  }, [data.id]);

  const handleClick = () => {
    setUserState({ ...userState, selectedMentee: data });
    if (path) {
      return navigate(path, {
        state: { ...pathSate, ...state },
      });
    }

    if (userState.activeNav === 'All Quarterly Reviews') {
      //Through mentee management
      navigate(
        `${Paths.AssignedMentees}/${convertNameToPath(quarterName ? quarterName : '')}/${convertNameToPath(data.name)}`,
        { state: { from: 'mentor', ...state } }
      );
    } else if (userState.activeNav === 'Employee Dashboard') {
      //Through Hr pages
      navigate(`${Paths.EmployeeDashboard}/${convertNameToPath(data.name)}`, {
        state: { ...state },
      });
    } else {
      //Through Hr pages
      navigate(
        `${Paths.HRAllReviews}/${convertNameToPath(quarterName ? quarterName : '')}/${convertNameToPath(data.name)}`,
        { state: { ...state } }
      );
    }
  };

  return (
    <div onClick={handleClick} className="cursor-pointer hover:text-[#69b1ff]">
      <div className="flex items-center">
        {url !== '' ? (
          <img src={url} alt="Profile" className="rounded-full w-14 h-14" />
        ) : (
          <div
            className={`rounded-full w-14 h-14 flex items-center justify-center text-white ${getRandomColor(data.name)}`}
          >
            {getInitials(data.name)}
          </div>
        )}
        <span className="capitalize font-[500] ml-2">{data.name}</span>
      </div>
    </div>
  );
};

export const QuarterName = ({ data }: { data: EmployeeData }) => {
  const [userState, setUserState] = useAtom(userAtom);
  const navigate = useNavigate();

  const handleClick = () => {
    setUserState({ ...userState, selectedMentee: data });
    if (userState.activeNav === 'Employee Dashboard') {
      navigate(
        `${Paths.QuarterlyReview}/${convertNameToPath(data?.name || '')}`,
        { state: { from: 'hr' } }
      );
    }
  };

  const currentCycle = data.quarterData?.find(
    (item: any) =>
      dayjs().isAfter(item.startDate) && dayjs().isBefore(item.endDate)
  );

  if (!data.cycleName || !data.cycleId || !currentCycle) {
    return <div className="text-center">-</div>;
  }

  const startDate = dayjs(currentCycle.startDate);

  const quarter = Math.ceil((startDate.month() + 1) / 3);
  const year = startDate.year();

  return (
    <div
      onClick={handleClick}
      className="text-left text-blue-600 underline cursor-pointer"
    >
      {`Q${quarter} - ${year}`}
    </div>
  );
};

export const AnnualCycle = ({
  data,
  year,
  annualCycleId,
  status,
}: {
  data: EmployeeData;
  year: string;
  annualCycleId: string;
  status: number;
}) => {
  const [userState, setUserState] = useAtom(userAtom);
  const navigate = useNavigate();

  const handleClick = () => {
    setUserState({
      ...userState,
      selectedMentee: data,
      cycleId: annualCycleId,
      quarterId: data.cycleId,
    });
    if (userState.activeNav === 'Employee Dashboard') {
      //Through Hr pages
      navigate(
        `${Paths.EmployeeDashboard}/${convertNameToPath(data?.name || '')}/annual-feedback`,
        { state: { from: 'hr' } }
      );
    }
  };

  return (
    <div
      onClick={handleClick}
      className="text-left text-blue-600 underline cursor-pointer"
    >
      {year}
      {annualReviewStatuses[status] === Statuses.InProgress
        ? '(In Progress)'
        : ''}
    </div>
  );
};
